import { useQuery } from '@apollo/client';
import { faBolt, faCalendar, faFileLines, faLocationDot, faTag, faTruck } from '@fortawesome/pro-regular-svg-icons';
import { camelCase } from 'lodash';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AssistantTabForm from './AssistantTabForm.tsx';
import ToggleCard from './ToggleCard.tsx';
import { useAssistantConfigForm } from './useAssistantConfigForm.ts';
import { withIcon } from '../../../../core/components/Icon.tsx';
import { GET_AVAILABLE_SKILLS } from '../../queries.ts';
import { AGENT_CONFIG_STEPS } from '../config/ConfigSteps.tsx';

const SKILL_ICONS = {
    availability: withIcon(faCalendar),
    deliveryDate: withIcon(faTruck),
    priceRequest: withIcon(faTag),
    productSpecification: withIcon(faFileLines),
    trackingLink: withIcon(faLocationDot),
};
const DefaultSkillIcon = withIcon(faBolt);

// the `skills.*` key a skill's copy lives under, the same as in the agents config
const skillTranslationKey = (key: string) => camelCase(key || '').replace('Skill', '');

interface AssistantSkillsTabProps {
    agent: any;
    onChange: () => Promise<unknown>;
}

/** The Skills tab: the types of requests the assistant answers, each switched on or off. */
const AssistantSkillsTab = ({ agent, onChange }: AssistantSkillsTabProps) => {
    const { t } = useTranslation('assistants');
    const { t: tAgentConfig } = useTranslation('assistants', { keyPrefix: 'agentConfig' });
    const { form, isSubmitting, onSubmit } = useAssistantConfigForm(agent, onChange);

    const { data } = useQuery(GET_AVAILABLE_SKILLS);

    // the assistants copy where the design has one, the assistants' own agentConfig copy for any other skill the
    // backend offers
    const skillName = (key: string) =>
        t(`detail.skillsTab.skills.${key}.name`, { defaultValue: tAgentConfig(`skills.${key}.name`) });
    const skillDescription = (key: string) =>
        t(`detail.skillsTab.skills.${key}.description`, { defaultValue: tAgentConfig(`skills.${key}.description`) });

    const skills = (data?.availableSkills || []).toSorted((skillA, skillB) =>
        skillName(skillTranslationKey(skillA.key)).localeCompare(skillName(skillTranslationKey(skillB.key)))
    );

    return (
        <AssistantTabForm
            title={t('detail.skillsTab.title')}
            description={t('detail.skillsTab.description')}
            form={form}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            isValid={AGENT_CONFIG_STEPS.skills.isValid!(form)}
        >
            <Controller
                name="skillNames"
                control={form.control}
                render={({ field }) => (
                    <div className="flex flex-col gap-2">
                        {skills.map((skill) => {
                            const key = skillTranslationKey(skill.key);
                            const Icon = SKILL_ICONS[key] || DefaultSkillIcon;
                            return (
                                <ToggleCard
                                    key={skill.key}
                                    icon={<Icon className="text-content-strong" />}
                                    title={skillName(key)}
                                    description={skillDescription(key)}
                                    isChecked={field.value.includes(skill.key)}
                                    onIsCheckedChange={(isChecked) => {
                                        const current: string[] = form.getValues('skillNames') || [];
                                        field.onChange(
                                            isChecked
                                                ? [...current, skill.key]
                                                : current.filter((skillKey) => skillKey !== skill.key)
                                        );
                                    }}
                                />
                            );
                        })}
                    </div>
                )}
            />
        </AssistantTabForm>
    );
};

export default AssistantSkillsTab;
